import { ActionName } from '@invenco/common-domain/enums';

import {
  type EntityQuery,
  type FieldDescriptor,
  entitySelect,
  multiSelect,
  number,
  optionsFromValues,
  select,
  text,
} from './field-descriptor';

/** A single parameter an action collects. */
export type ActionParam = {
  /** The key written into the action's `params` payload. */
  key: string;
  /** Sentence-case field label. */
  label: string;
  input: FieldDescriptor;
  required?: boolean;
};

/** The presentation and parameters for one action type. */
export type ActionDefinition = {
  /** Sentence-case title, read as the action card heading. */
  title: string;
  params: ActionParam[];
  /** Backend-recognised but not yet supported in the UI. */
  disabled?: boolean;
};

// --- Entity query helpers ---

const skuQuery: EntityQuery = ({ productsGateway }, input, options) =>
  productsGateway.getSkus(input, options);

const shippingProfileQuery: EntityQuery = ({ salesGateway }, input, options) =>
  salesGateway.getShippingProfiles(input, options);

const fulfillmentProfileQuery: EntityQuery = ({ salesGateway }, input, options) =>
  salesGateway.getFulfillmentProfiles(input, options);

const skuParamInput = entitySelect({ query: skuQuery, parentKey: 'action-skus', valueKey: 'name' });

/**
 * One entry per `ActionName`. The `Record` annotation forces full enum coverage:
 * a missing action fails compilation. Titles use sentence-case copy.
 */
export const actionDefinitions: Record<ActionName, ActionDefinition> = {
  ENSURE_ORDER_LINE: {
    title: 'Ensure the order contains',
    params: [
      { key: 'skuName', label: 'SKU name', required: true, input: skuParamInput },
      { key: 'unitPrice', label: 'Unit price', required: true, input: number() },
      { key: 'description', label: 'Description', input: text() },
      { key: 'qtyOrdered', label: 'Qty ordered', required: true, input: number({ integer: true }) },
    ],
  },
  ENSURE_ORDER_LINE_DYNAMICALLY: {
    title: 'For every SKU in group then ensure order contains',
    params: [
      {
        key: 'referenceSkuNames',
        label: 'For each ordered quantity of SKUs in',
        required: true,
        input: entitySelect({
          query: skuQuery,
          parentKey: 'action-skus',
          valueKey: 'name',
          multi: true,
        }),
      },
      { key: 'skuName', label: 'Ensure order contains one', required: true, input: skuParamInput },
      { key: 'unitPrice', label: 'Unit price', required: true, input: number() },
      { key: 'description', label: 'Description', input: text() },
    ],
  },
  APPLY_MANUAL_HOLD: { title: 'Apply a manual hold', params: [] },
  SET_SHIPPING_PROFILE: {
    title: "Set the order's shipping profile to",
    params: [
      {
        key: 'shippingProfileId',
        label: 'Shipping profile',
        required: true,
        input: entitySelect({
          query: shippingProfileQuery,
          parentKey: 'action-shipping-profiles',
          valueKey: 'id',
          labelKey: 'name',
          preloadAll: true,
        }),
      },
    ],
  },
  SET_DELIVERY_INSTRUCTIONS: {
    title: "Set the order's delivery instructions to",
    params: [
      {
        key: 'deliveryInstructions',
        label: 'Delivery instructions',
        required: true,
        input: text(),
      },
    ],
  },
  SET_PACKING_INSTRUCTIONS: {
    title: "Set the order's packing instructions to",
    params: [
      { key: 'packingInstructions', label: 'Packing instructions', required: true, input: text() },
    ],
  },
  CREATE_FIRST_FULFILLMENT: { title: 'Create the first fulfillment', params: [] },
  SET_WAREHOUSE_OPERATION_OPTIONS: {
    title: 'Set the warehouse operation options',
    params: [
      {
        key: 'packSlipPrint',
        label: 'Pack slip print',
        input: select({ options: optionsFromValues(['NO_PRINT', 'PRINT_LABEL', 'PRINT_A4']) }),
      },
      {
        key: 'allowablePickStrategies',
        label: 'Allowable pick strategies',
        input: multiSelect({
          options: optionsFromValues(['DEFAULT', 'BATCH', 'DISCRETE', 'SINGLE', 'TOTE']),
        }),
      },
      { key: 'workStreamName', label: 'Workstream name', input: text() },
      { key: 'jobType', label: 'Job type', input: text() },
      { key: 'packMethod', label: 'Pack method', input: text() },
      { key: 'labelTemplate', label: 'Label template', input: text() },
    ],
  },
  SET_ORDER_FULFILLMENT_PROFILE: {
    title: "Set the order's fulfillment profile to",
    params: [
      {
        key: 'fulfillmentProfileId',
        label: 'Fulfillment profile',
        required: true,
        input: entitySelect({
          query: fulfillmentProfileQuery,
          parentKey: 'action-fulfillment-profiles',
          valueKey: 'id',
          labelKey: 'name',
          preloadAll: true,
        }),
      },
    ],
  },

  // Backend-recognised but not yet supported in the UI.
  RELEASE_ORDER: { title: 'Release the order', params: [], disabled: true },
  REVERT_ORDER_TO_DRAFT: { title: 'Revert the order to draft', params: [], disabled: true },
  ADD_ORDER_LINE: { title: 'Add a line to the order', params: [], disabled: true },
  SET_ORDER_LINE: { title: 'Set a line on the order', params: [], disabled: true },
  TRIGGER_RULE: { title: 'Trigger another rule', params: [], disabled: true },
};
